import { lazy, ReactNode } from 'react';

// project-imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'components/Loadable';
import CommonLayout from 'layout/CommonLayout';
import AccountGuard from 'utils/route-guard/AccountGuard';
import AuthGuard from 'utils/route-guard/AuthGuard';
import PermissionGuard from 'utils/route-guard/PermissionGuard';

// types
import { Permission } from 'utils/permissions';

// render - developer
const Apps = Loadable(lazy(() => import('pages/developer/Apps')));
const AppDetail = Loadable(lazy(() => import('pages/developer/AppDetail')));
const ReleaseDetail = Loadable(lazy(() => import('pages/developer/ReleaseDetail')));
const Keys = Loadable(lazy(() => import('pages/developer/Keys')));
const Publishing = Loadable(lazy(() => import('pages/developer/Publishing')));
const Verification = Loadable(lazy(() => import('pages/developer/Verification')));
const Team = Loadable(lazy(() => import('pages/developer/Team')));
const Account = Loadable(lazy(() => import('pages/developer/Account')));

// render - account sign-up and invites (signed in, outside the dashboard chrome)
const Onboarding = Loadable(lazy(() => import('pages/developer/Onboarding')));
const InviteAccept = Loadable(lazy(() => import('pages/developer/InviteAccept')));

// render - internal testing (any signed-in user, a developer account is not needed)
const MyTesting = Loadable(lazy(() => import('pages/developer/MyTesting')));
const TestingJoin = Loadable(lazy(() => import('pages/developer/TestingJoin')));

// render - platform
const Overview = Loadable(lazy(() => import('pages/platform/Overview')));
const Moderation = Loadable(lazy(() => import('pages/platform/Moderation')));
const Products = Loadable(lazy(() => import('pages/platform/Products')));
const ProductDetail = Loadable(lazy(() => import('pages/platform/ProductDetail')));
const Developers = Loadable(lazy(() => import('pages/platform/Developers')));
const Desktop = Loadable(lazy(() => import('pages/platform/Desktop')));
const Segments = Loadable(lazy(() => import('pages/platform/Segments')));
const SegmentTag = Loadable(lazy(() => import('pages/platform/SegmentTag')));
const SegmentDetail = Loadable(lazy(() => import('pages/platform/SegmentDetail')));
const News = Loadable(lazy(() => import('pages/platform/News')));
const NewsEditor = Loadable(lazy(() => import('pages/platform/NewsEditor')));
const Models = Loadable(lazy(() => import('pages/platform/Models')));
const Payments = Loadable(lazy(() => import('pages/platform/Payments')));
const Issues = Loadable(lazy(() => import('pages/platform/Issues')));
const IssueDetail = Loadable(lazy(() => import('pages/platform/IssueDetail')));
const Audit = Loadable(lazy(() => import('pages/platform/Audit')));
const Admins = Loadable(lazy(() => import('pages/platform/Admins')));

const NotFound = Loadable(lazy(() => import('pages/NotFound')));

/** Every /platform route is wrapped, so a bookmark to it explains itself rather than going blank. */
const platform = (permission: Permission, element: ReactNode) => <PermissionGuard permission={permission}>{element}</PermissionGuard>;

// ==============================|| MAIN ROUTES ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        // Developer: every signed-in user. Someone with no developer account is sent to /onboarding first,
        // and the pages remount when the active account changes.
        {
          element: <AccountGuard />,
          children: [
            { path: 'apps', element: <Apps /> },
            { path: 'apps/:id', element: <AppDetail /> },
            { path: 'apps/:id/releases/:rid', element: <ReleaseDetail /> },
            { path: 'keys', element: <Keys /> },
            { path: 'docs/publishing', element: <Publishing /> },
            { path: 'verification', element: <Verification /> },
            { path: 'team', element: <Team /> },
            { path: 'account', element: <Account /> }
          ]
        },

        // Tester pages: for everyone signed in, so they sit outside the account guard.
        { path: 'testing', element: <MyTesting /> },

        // Platform: every page is also gated by its permission.
        { path: 'platform', element: platform('overview.read', <Overview />) },
        { path: 'platform/moderation', element: platform('products.review', <Moderation />) },
        { path: 'platform/products', element: platform('products.read', <Products />) },
        { path: 'platform/products/:id', element: platform('products.read', <ProductDetail />) },
        { path: 'platform/developers', element: platform('developers.read', <Developers />) },
        { path: 'platform/desktop', element: platform('releases.client.publish', <Desktop />) },
        { path: 'platform/segments', element: platform('segments.read', <Segments />) },
        // Static paths first, so `new` and `tags/:tag` are never read as a segment id.
        { path: 'platform/segments/new', element: platform('segments.read', <Segments creating />) },
        { path: 'platform/segments/tags/:tag', element: platform('segments.read', <SegmentTag />) },
        { path: 'platform/segments/:id', element: platform('segments.read', <SegmentDetail />) },
        { path: 'platform/news', element: platform('news.read', <News />) },
        { path: 'platform/news/new', element: platform('news.read', <NewsEditor />) },
        { path: 'platform/news/:id', element: platform('news.read', <NewsEditor />) },
        { path: 'platform/models', element: platform('models.read', <Models />) },
        { path: 'platform/payments', element: platform('payments.read', <Payments />) },
        { path: 'platform/errors', element: platform('errors.read', <Issues />) },
        { path: 'platform/errors/:id', element: platform('errors.read', <IssueDetail />) },
        { path: 'platform/audit', element: platform('audit.read', <Audit />) },
        { path: 'platform/admins', element: platform('admins.read', <Admins />) }
      ]
    },

    {
      // Signed in, but without the dashboard around them. The invite page lands here after the SSO round
      // trip: AuthGuard carries the path as `returnTo`.
      element: (
        <AuthGuard>
          <CommonLayout />
        </AuthGuard>
      ),
      children: [
        { path: 'onboarding', element: <Onboarding /> },
        { path: 'invite/:token', element: <InviteAccept /> },
        { path: 'testing/join/:token', element: <TestingJoin /> }
      ]
    },

    { path: '*', element: <NotFound /> }
  ]
};

export default MainRoutes;
